import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Avatar from '../components/Avatar.jsx'
import FeedCard from '../components/FeedCard.jsx'
import FollowButton from '../components/FollowButton.jsx'
import ErrorState, { LoadingState } from '../components/ErrorState.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import { ApiError, api } from '../lib/api.js'
import { useAuth } from '../context/AuthContext.jsx'

/**
 * `/` — the feed (FEED-001, PLAN §7).
 *
 * Fan-out on read: the server takes the events of the people you follow and
 * hands them back newest first. Nothing is stored per reader, so following
 * somebody backfills their history immediately rather than starting a clock.
 *
 * The empty state is the important one. A closed alpha's first screen is
 * empty by construction, and "no posts yet" would be a dead end — so an empty
 * feed becomes the people-to-follow screen (PLAN §7, "Empty → suggestions").
 */
function Suggestions({ people, onFollow }) {
  return (
    <div className="card card-pad fade-up">
      <div className="empty-state">
        <h2>Your feed is quiet</h2>
        <p>
          Follow a few people and their ratings, reviews and lists land here as they happen.
        </p>
      </div>
      {people?.length ? (
        <ul className="suggestion-list">
          {people.map((person) => (
            <li key={person.public_id} className="suggestion">
              <Link to={`/profile/${person.username}`} className="suggestion-who">
                <Avatar name={person.display_name || person.username} size={40} />
                <span className="row-main">
                  <span className="feed-actor-name">{person.display_name || person.username}</span>
                  <span className="footnote">@{person.username}</span>
                </span>
              </Link>
              <FollowButton handle={person.username} state={null} onChange={onFollow} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="footnote">
          Nobody else has posted yet. <Link to="/search">Find something to rate</Link> and yours
          will be the first.
        </p>
      )}
    </div>
  )
}

export default function Feed() {
  const { user } = useAuth()
  const [state, setState] = useState({ loading: true, items: [], suggestions: [], next: null })
  const [error, setError] = useState('')
  const [moreError, setMoreError] = useState('')
  // A failed heart belongs to the card it was pressed on, so the message is
  // keyed by card rather than shared by the page.
  const [cardErrors, setCardErrors] = useState({})

  const load = useCallback(async (signal) => {
    setError('')
    try {
      const body = await api.feed({ signal })
      setState({
        loading: false,
        items: body.items,
        suggestions: body.suggestions,
        next: body.next_before_id,
      })
    } catch (err) {
      if (err.name === 'AbortError') return
      // Worded to match the ErrorState title below, so its duplicate guard
      // catches this and the page says it once rather than twice.
      setError(err instanceof ApiError ? err.message : 'Your feed did not load.')
      setState((current) => ({ ...current, loading: false }))
    }
  }, [])

  useEffect(() => {
    document.title = 'Sonologue'
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  async function more() {
    if (!state.next) return
    setMoreError('')
    try {
      const body = await api.feed({ beforeId: state.next })
      setState((current) => ({
        ...current,
        items: [...current.items, ...body.items],
        next: body.next_before_id,
      }))
    } catch (err) {
      // The button stays, so pressing it again is the retry.
      setMoreError(err instanceof ApiError ? err.message : 'Older posts did not load.')
    }
  }

  async function react(card) {
    setCardErrors((current) => ({ ...current, [card.id]: '' }))
    // Optimistic, then reconciled with what the server says the count is.
    setState((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === card.id
          ? {
              ...item,
              reacted: !item.reacted,
              reaction_count: item.reaction_count + (item.reacted ? -1 : 1),
            }
          : item,
      ),
    }))
    try {
      const result = await api.react('feed_event', card.public_id)
      setState((current) => ({
        ...current,
        items: current.items.map((item) =>
          item.id === card.id
            ? { ...item, reacted: result.reacted, reaction_count: result.count }
            : item,
        ),
      }))
    } catch (err) {
      // Undo the optimistic step on this card and no other. Reloading the
      // feed put the count back too, but it refetched page one — so a reader
      // who had pressed *Older* was collapsed back to the top, and a reload
      // that failed in its turn replaced the whole feed with an error.
      setState((current) => ({
        ...current,
        items: current.items.map((item) =>
          item.id === card.id
            ? { ...item, reacted: card.reacted, reaction_count: card.reaction_count }
            : item,
        ),
      }))
      setCardErrors((current) => ({
        ...current,
        [card.id]: err instanceof ApiError ? err.message : 'Could not save that just now.',
      }))
    }
  }

  if (state.loading) return <LoadingState label="Loading your feed" />

  if (error) {
    return (
      <ErrorState
        title="Your feed did not load"
        message={error}
        onRetry={() => {
          setState((current) => ({ ...current, loading: true }))
          load()
        }}
      />
    )
  }

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">Feed</h1>
        {user?.username ? (
          <Link className="btn btn-quiet btn-sm" to={`/profile/${user.username}`}>
            Your profile
          </Link>
        ) : null}
      </div>

      {state.items.length === 0 ? (
        <Suggestions people={state.suggestions} onFollow={() => load()} />
      ) : (
        <div className="feed">
          {state.items.map((card) => (
            <FeedCard key={card.id} card={card} error={cardErrors[card.id]} onReact={react} />
          ))}
          {state.next ? (
            <button type="button" className="btn btn-tonal" onClick={more}>
              Older
            </button>
          ) : null}
          <InlineAlert>{moreError}</InlineAlert>
        </div>
      )}
    </>
  )
}
